import { createFileRoute, Link } from "@tanstack/react-router";
import {
  LearnArticleLayout,
  learnArticleHead,
} from "@/components/LearnArticleLayout";

const PATH = "/learn/landscaper-marketing-cost-colorado";
const TITLE = "How Much Does Landscaper Marketing Cost in Colorado? (2026)";
const DESCRIPTION =
  "What landscaping SEO, Google Maps, ads, and websites actually cost in Colorado in 2026 — by city and by season, with real cost-per-lead numbers.";

export const Route = createFileRoute("/learn/landscaper-marketing-cost-colorado")({
  head: () => learnArticleHead({ title: TITLE, description: DESCRIPTION, path: PATH }),
  component: Article,
});

function Article() {
  return (
    <LearnArticleLayout
      category="Pricing"
      title={TITLE}
      intro="Landscaping is highly seasonal in Colorado — and most landscapers either over-spend in summer or completely disappear in winter. Here's what a real, year-round marketing program costs in 2026."
      readTime="5 min read"
      publishedDate="2026-06-22"
      path={PATH}
      definition={{
        term: "What does landscaping marketing cost in Colorado?",
        body: "Most Colorado landscapers invest $1,500–$3,500/month for SEO, Google Maps, and AI search, plus $1,500–$8,000/month in seasonal Google Ads spend. A custom landscaping website starts at $1,850 one-time. Most ROI comes from booking spring/summer work in February–April.",
      }}
      toc={[
        { id: "seasonality", label: "Seasonality changes the math" },
        { id: "real-numbers", label: "Real Colorado landscaping numbers" },
        { id: "by-city", label: "Cost by city" },
        { id: "cost-per-lead", label: "What a landscaping lead costs" },
        { id: "design-build-vs-maintenance", label: "Design-build vs. maintenance budgets" },
      ]}
      faqs={[
        {
          q: "When should I start marketing for a Colorado landscaping season?",
          a: "December–February. Buyers research and book design-build projects 8–16 weeks before they want them installed. Companies that wait until April are already behind — their competitors are quoting work they should be quoting.",
        },
        {
          q: "Should I pause marketing in Colorado winter?",
          a: "No. Cut PPC ad spend, but keep SEO, Maps, and content running. Winter is when you build the rankings you'll cash in next May. Pausing means rebuilding lost ground every spring.",
        },
        {
          q: "What's the right marketing budget for a $750k Colorado landscape company?",
          a: "8–12% of revenue, weighted to Q1 and Q2. That's roughly $5k–$7.5k/month average, peaking at $10k+ in March–May. Our $1,650/month Growth Marketing program plus seasonal Google Ads spend is the typical structure.",
        },
        {
          q: "Do landscapers need a custom website in Colorado?",
          a: "Yes. Landscaping is a visual sale — homeowners compare portfolios. A template site without real Colorado project photos converts 40–60% worse than a custom site with proper gallery, service pages, and process content.",
        },
      ]}
    >
      <h2 id="seasonality">Seasonality changes the math</h2>
      <p>
        Landscaping isn't a flat 12-month marketing problem. Roughly 70% of
        annual booked revenue gets quoted in February–May. That means your
        marketing dollars should also be front-loaded:
      </p>
      <ul>
        <li><strong>Dec–Feb</strong> — heavy SEO + content + GBP, light ads.</li>
        <li><strong>Mar–May</strong> — heaviest period; peak PPC + Maps + retargeting.</li>
        <li><strong>Jun–Aug</strong> — moderate; brand and reviews.</li>
        <li><strong>Sep–Nov</strong> — pivot to fall cleanup, holiday lighting, snow services.</li>
      </ul>

      <h2 id="real-numbers">Real Colorado landscaping numbers</h2>
      <h3>2–5 truck residential landscaper</h3>
      <p>
        $1,850 site + $1,650/month{" "}
        <Link to="/pricing" className="text-primary underline">Growth Marketing</Link>{" "}
        + $2,000–$4,000/month ad spend in season. First-year total ~$55–65k.
      </p>
      <h3>Multi-crew design-build firm</h3>
      <p>
        $5,000–$10,000 custom portfolio site + $3,500/month managed SEO/Maps +
        $4,000–$8,000/month ad spend in spring.
      </p>

      <h2 id="by-city">Cost by city</h2>
      <ul>
        <li><Link to="/denver-seo-cost" className="text-primary underline">Denver SEO cost</Link></li>
        <li><Link to="/colorado-springs-seo-cost" className="text-primary underline">Colorado Springs SEO cost</Link></li>
        <li><Link to="/boulder-seo-cost" className="text-primary underline">Boulder SEO cost</Link> — premium-priced design-build market.</li>
        <li><Link to="/castle-rock-seo-cost" className="text-primary underline">Castle Rock SEO cost</Link></li>
      </ul>
      <p>
        For market-specific examples see our{" "}
        <Link to="/colorado-springs-landscaper-seo" className="text-primary underline">
          Colorado Springs landscaper SEO
        </Link>{" "}
        and{" "}
        <Link to="/landscaping-marketing-colorado" className="text-primary underline">
          Colorado landscaping marketing hub
        </Link>
        .
      </p>

      <h2 id="cost-per-lead">What a landscaping lead costs</h2>
      <ul>
        <li>Google Ads (residential install): $80–$200 per qualified lead.</li>
        <li>Google Maps: $15–$45 per call once ranked top-3.</li>
        <li>Organic SEO: $20–$60 effective cost per lead after the first season.</li>
      </ul>

      <h2 id="design-build-vs-maintenance">Design-build vs. maintenance budgets</h2>
      <p>
        Design-build leads are worth $5,000–$50,000+ each, so a $200 cost-per-lead
        is still wildly profitable. Maintenance leads are worth $80–$300/month
        recurring — keep CPL under $60 or the unit economics break. Most healthy
        Colorado landscapers run two campaigns, not one.
      </p>
      <p>
        Full pricing on the{" "}
        <Link to="/pricing" className="text-primary underline">pricing page</Link>{" "}
        — all month-to-month.
      </p>
    </LearnArticleLayout>
  );
}
